import { toast } from "@/components";
import { Checkbox } from "@/components/ui";
import { cn, planStatus } from "@/lib/utils";
import { useGetPlanListByProjectId, useUpdatedPlanStatusMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const useProjectPlan = () => {
  const { id } = useParams();
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isApprove, setIsApprove] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: planList,
    isLoading: isPlanLoading,
  } = useGetPlanListByProjectId({ projectId: id as string });

  const isApprovePlanExist = useMemo(() => {
    if (planList?.data) {
      return planList?.data?.some((data: any) => data?.status == planStatus.approved.value)
    }
  }, [planList])
  
  // Define Table Columns
  const columns = [
    ...isApprovePlanExist ? [] : [{
      accessorKey: "projectPlanId",
      header: "",
      cell: (row: any) => (
        <Checkbox
          disabled={row?.row?.original?.status != planStatus.approval_pending.value}
          key={`plan-${row?.row?.original?.projectPlanId}`}
          checked={selectedPlan === row?.row?.original?.projectPlanId}
          onCheckedChange={(checked) => {
            if (checked === true) {
              setSelectedPlan(row?.row?.original?.projectPlanId);
            }
          }}
        />
      ),
      size: 100,
    }],
    {
      accessorKey: "projectPlanId",
      header: "ID",
      accessorFn: (row: any) => String(row.projectPlanId),
      size: 100,
    },
    {
      accessorKey: "name",
      header: "Plan Name",
      cell: ({ row }: any) => {
        return row.original.name ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`${row.original.projectPlanId}/view`);
            }}
          >
            {String(row.original.name)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "version",
      header: "Version",
      accessorFn: (row: any) => String(row?.version || ""),
      size: 100,
    },
    {
      accessorKey: "taskCount",
      header: "# Task",
      accessorFn: (row: any) => String(row?.taskCount || ""),
      size: 100,
    },
    {
      accessorKey: "duration",
      header: "Duration (Days)",
      accessorFn: (row: any) => String(row?.duration || ""),
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Plan Status",
      cell: (row: any) => (
        <span
          className={cn(
            "font-[600]",
            planStatus[row?.row?.original?.status?.toLowerCase()]?.className
          )}
        >
          {planStatus[row?.row?.original?.status?.toLowerCase()]?.label ||
            ""}
        </span>
      ),
      size: 100,
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) => row?.createdAt ? moment(row?.createdAt).format('MM-DD-YYYY') : "",
      size: 100,
    },
    {
      accessorKey: "updatedAt",
      header: "Updated On",
      accessorFn: (row: any) => row?.updatedAt ? moment(row?.updatedAt).format('MM-DD-YYYY') : "",
      size: 100,
    },
    {
      accessorKey: "updatedByName",
      header: "Updated By",
      accessorFn: (row: any) => row?.updatedByName || "",
      size: 100,
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onSubmitApproval = (approve: boolean) => () => {
    setIsApprove(approve);
    setShowConfirmation(true);
  }

  const onChangeReason = (e: any) => {
    setReason(e.target.value)
  }

  const { mutate: updateStatus, isPending: isUpdatePending } = useUpdatedPlanStatusMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["plan-list", id],
      });
      setShowConfirmation(false);
      setReason("");
      setSelectedPlan("");
      toast({ message: "Plan status updated successfully." });
    },
  });

  const onConfirmSubmit = () => {
    if (reason?.length < 21) {
      toast({type: 'error', message: 'Minimum 20 character required for reason'});
      return;
    }

    updateStatus({
      planId: selectedPlan,
      projectId: id as string,
      data: {
        action: isApprove ? planStatus.approved.value : planStatus.rejected.value,
        comment: reason
      }
    })
  }

  return {
    globalFilter,
    setGlobalFilter,
    columns,
    onClose,
    openDialog,
    planList,
    isPlanLoading,
    selectedPlan,
    onSubmitApproval,
    showConfirmation,
    setShowConfirmation,
    reason,
    onChangeReason,
    onConfirmSubmit,
    isUpdatePending,
    isApprove,
    isApprovePlanExist
  };
};

export default useProjectPlan;
